/**
 * DisplayMessage renderer · iter303ai-fix8
 *
 * Static-copy display step with dynamic-token substitution. Used by the
 * Overwatch runner to show contextual "Thank you" or transition messages
 * between capture steps.
 *
 * action.params:
 *   • body       — literal string (falls back to a generic message)
 *   • copy_map   — { key: string } — swap the body based on the value of
 *                  `switch_on` in captured_data. Example:
 *                    {
 *                      switch_on: "appraisal.report_variant",
 *                      copy_map: {
 *                        free_estimate: "Thanks — we'll have your free estimate emailed soon.",
 *                        single_defect: "Great — we'll book your single-defect appraisal now.",
 *                        ...
 *                      }
 *                    }
 *   • switch_on  — captured_data key whose value picks a `copy_map` entry
 *   • title      — small pill above the message (default "Message")
 *
 * The renderer intentionally has no `write_to` — this is a display step
 * only. Advances immediately when the operator clicks Continue.
 */
import React from 'react';
import { MessageSquare } from 'lucide-react';

// iter303ai-fix8 · Robust dotted-key lookup. Handles both flat and nested
// captured_data shapes — see WorkflowRunnerPage.readKey for context.
function readDotted(obj, key) {
  if (!obj || !key) return undefined;
  // 1. Flat key (write_to='appraisal.report_variant' stores flat).
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  // 2. Nested walk fallback.
  const parts = key.split('.');
  let v = obj;
  for (const p of parts) {
    if (v == null || typeof v !== 'object') return undefined;
    v = v[p];
  }
  return v;
}

export default function DisplayMessage({ action, capturedData, onNext, onBack, canBack }) {
  const params = action.params || {};
  const switchVal = readDotted(capturedData, params.switch_on);
  const copyMap = params.copy_map || {};
  const body =
    (switchVal && copyMap[switchVal]) ||
    params.body ||
    'Continue when ready.';
  const title = params.title || 'Message';

  return (
    <div>
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-6 mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-2">
          <MessageSquare size={12} /> {title}
        </div>
        <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-message-body">
          {body}
        </div>
      </div>

      <div className="flex items-center gap-3">
        {canBack && (
          <button
            onClick={onBack}
            data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">
            ← Back
          </button>
        )}
        <span className="flex-1" />
        <button
          onClick={onNext}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700">
          Continue →
        </button>
      </div>
    </div>
  );
}
